import { useState, useEffect } from 'react';
import { Modal, Button } from '@components/ui';
import { mockBlocks } from '@mocks/bloklar';

export default function DaireModal({ isOpen, onClose, onSubmit, apartment = null, constructionId }) {
    const isEdit = !!apartment;

    const [formData, setFormData] = useState({
        blockId: '',
        floor: '',
        number: '',
        grossM2: '',
        netM2: '',
        unitPrice: '',
        status: 'available',
        buyerName: '',
        paymentType: '',
        paidAmount: '',
    });

    useEffect(() => {
        if (apartment) {
            setFormData({
                blockId: apartment.blockId || '',
                floor: apartment.floor || '',
                number: apartment.number || '',
                grossM2: apartment.grossM2 || '',
                netM2: apartment.netM2 || '',
                unitPrice: apartment.unitPrice || '',
                status: apartment.status || 'available',
                buyerName: apartment.buyerName || '',
                paymentType: apartment.paymentType || '',
                paidAmount: apartment.paidAmount || '',
            });
        } else {
            setFormData({
                blockId: '', floor: '', number: '', grossM2: '', netM2: '',
                unitPrice: '', status: 'available', buyerName: '', paymentType: '', paidAmount: '',
            });
        }
    }, [apartment, isOpen]);

    const blocks = constructionId
        ? mockBlocks.filter(b => b.constructionId === constructionId)
        : mockBlocks;

    const totalPrice = (Number(formData.grossM2) || 0) * (Number(formData.unitPrice) || 0);

    const handleSubmit = () => {
        const selectedBlock = mockBlocks.find(b => b.id === formData.blockId);
        onSubmit?.({
            ...formData,
            blockName: selectedBlock?.name || '',
            totalPrice,
            remainingAmount: totalPrice - (Number(formData.paidAmount) || 0),
        });
        onClose();
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? '🏠 Daire Düzenle' : '🏠 Yeni Daire Ekle'}
            size="lg"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>İptal</Button>
                    <Button variant="primary" onClick={handleSubmit}>
                        {isEdit ? 'Güncelle' : 'Kaydet'}
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                {/* Blok ve Kat */}
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Blok *</label>
                        <select
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            value={formData.blockId}
                            onChange={(e) => handleChange('blockId', e.target.value)}
                        >
                            <option value="">Seçiniz</option>
                            {blocks.map(block => (
                                <option key={block.id} value={block.id}>{block.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Kat *</label>
                        <input
                            type="number"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            placeholder="1"
                            value={formData.floor}
                            onChange={(e) => handleChange('floor', e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Daire No *</label>
                        <input
                            type="text"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            placeholder="A-101"
                            value={formData.number}
                            onChange={(e) => handleChange('number', e.target.value)}
                        />
                    </div>
                </div>

                {/* m² Bilgileri */}
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Brüt m²</label>
                        <input
                            type="number"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            placeholder="120"
                            value={formData.grossM2}
                            onChange={(e) => handleChange('grossM2', e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Net m²</label>
                        <input
                            type="number"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            placeholder="105"
                            value={formData.netM2}
                            onChange={(e) => handleChange('netM2', e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Birim Fiyat (₺/m²)</label>
                        <input
                            type="number"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            placeholder="25000"
                            value={formData.unitPrice}
                            onChange={(e) => handleChange('unitPrice', e.target.value)}
                        />
                    </div>
                </div>

                {/* Toplam Fiyat (readonly) */}
                <div className="bg-bg-tertiary rounded-lg p-4">
                    <div className="flex justify-between items-center">
                        <span className="text-text-secondary">Toplam Fiyat</span>
                        <span className="text-2xl font-bold text-primary">
                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(totalPrice)}
                        </span>
                    </div>
                </div>

                {/* Durum ve Ödeme */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Durum</label>
                        <select
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            value={formData.status}
                            onChange={(e) => handleChange('status', e.target.value)}
                        >
                            <option value="available">Boş</option>
                            <option value="reserved">Rezerve</option>
                            <option value="sold">Satıldı</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Ödeme Tipi</label>
                        <select
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                            value={formData.paymentType}
                            onChange={(e) => handleChange('paymentType', e.target.value)}
                            disabled={formData.status === 'available'}
                        >
                            <option value="">Seçiniz</option>
                            <option value="pesin">Peşin</option>
                            <option value="taksit">Taksit</option>
                            <option value="arsa">Arsa Payı</option>
                            <option value="kapora">Kapora</option>
                        </select>
                    </div>
                </div>

                {/* Alıcı ve Ödenen */}
                {formData.status !== 'available' && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Alıcı Adı</label>
                            <input
                                type="text"
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                                placeholder="Müşteri adı"
                                value={formData.buyerName}
                                onChange={(e) => handleChange('buyerName', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Ödenen Tutar (₺)</label>
                            <input
                                type="number"
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary"
                                placeholder="0"
                                value={formData.paidAmount}
                                onChange={(e) => handleChange('paidAmount', e.target.value)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
